import {
  Card,
  CardHeader,
  CardHeaderProps as ChakraUICardHeaderProps,
  Flex,
  Avatar,
  Text,
  Icon,
  Image,
  Skeleton,
  SkeletonCircle,
  SkeletonText
} from "@chakra-ui/react";
import { HiMapPin } from "react-icons/hi2";
import { ReactNode } from "react";

export interface CardHeaderProps extends ChakraUICardHeaderProps {
  username?: string;
  userAvatarSrc?: string;
  contributionTime?: string;
  imageSrc?: string;
  imageAlt?: string;
  showPin?: boolean;
  showImage?: boolean;
  hasHeaderHighlight?: boolean;
  cardVariant?: string;
  ariaLabelMapPinIcon?: string;
  ariaLabelVerticalDotsIcon?: string;
  handleMoreOptions?(): any;
  isLoaded?: boolean;
  showMoreOptions?: boolean;
  showCardBackground?: boolean;
  showHeaderColor?: boolean;
  switchHeaderDateColor?: boolean;
  switchOnSquareCard?: boolean;
  actionList?: ReactNode;
  cardSize?: number;
}

export function Header(props: CardHeaderProps) {
  const {
    username,
    userAvatarSrc,
    contributionTime,
    showPin,
    cardVariant,
    showImage,
    imageSrc,
    imageAlt,
    ariaLabelMapPinIcon,
    showMoreOptions,
    hasHeaderHighlight,
    isLoaded,
    showCardBackground,
    showHeaderColor,
    switchHeaderDateColor,
    switchOnSquareCard,
    actionList,
    cardSize
  } = props;

  return (
    <Card
      variant={cardVariant}
      boxShadow="none"
      borderRadius={switchOnSquareCard ? 0 : -1}
      bgColor={
        cardVariant === "elevated"
          ? "whiteAlpha.100"
          : showCardBackground
            ? "black"
            : "transparent"
      }
      color={showHeaderColor ? "white" : "black"}
    >
      <CardHeader p={0} className={hasHeaderHighlight ? "" : "no-highlight"}>
        {cardVariant === "elevated" && showImage && imageSrc && (
          <Skeleton isLoaded={isLoaded}>
            <Image
              minW="full"
              objectFit="cover"
              borderTopRadius="lg"
              data-testid="card-headerImage"
              src={imageSrc}
              alt={imageAlt}
              h="84px"
              cursor="pointer"
            />
          </Skeleton>
        )}
        <Flex p={cardVariant === "elevated" ? 2 : 0} pb={0}>
          <Flex flex={1} gap={2} alignItems="center" flexWrap="nowrap">
            <SkeletonCircle size="10" isLoaded={isLoaded}>
              <Avatar
                aria-hidden="true"
                data-testid="card-avatarImage"
                name={username}
                src={userAvatarSrc}
              />
            </SkeletonCircle>
            <Flex
              gap={!isLoaded ? 2 : 0}
              w="full"
              direction="column"
              cursor="default"
            >
              <Skeleton isLoaded={isLoaded} flexDirection="row">
                <Flex gap={0} direction="row" justifyContent="space-between">
                  <Text
                    width={cardSize && cardSize - 80}
                    noOfLines={1}
                    fontSize="sm"
                    fontWeight="semibold"
                    data-testid="card-usernameText"
                  >
                    {username}
                  </Text>
                </Flex>
              </Skeleton>
              <SkeletonText
                noOfLines={1}
                spacing={4}
                skeletonHeight={2}
                isLoaded={isLoaded}
                w="full"
              >
                <Text
                  color={switchHeaderDateColor ? "white" : "gray.700"}
                  fontSize="xs"
                  fontWeight="medium"
                  data-testid="card-contributionTimeText"
                >
                  {contributionTime}
                </Text>
              </SkeletonText>
            </Flex>
            {isLoaded && (
              <Flex gap={1} alignItems="center">
                {showPin && (
                  <Icon
                    justifySelf="flex-start"
                    aria-label={ariaLabelMapPinIcon}
                    minWidth={6}
                    as={HiMapPin}
                    w={6}
                    h={6}
                    color="purple.600"
                    data-testid="card-mapPinIcon"
                  />
                )}
                {showMoreOptions && actionList}
              </Flex>
            )}
          </Flex>
        </Flex>
      </CardHeader>
    </Card>
  );
}
